import { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, ReferenceLine, Cell, Tooltip } from 'recharts';
import { oteHistorical, oteTypicalDay, tariffBand } from '@/data/oteData';
import { t, getLocale } from '@/i18n';

const bandColors = {
  negative: '#3b82f6', // blue
  offpeak: '#10b981',  // emerald
  standard: '#f59e0b', // amber
  peak: '#ef4444',     // rose
};

function CustomTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const { hour, price } = payload[0].payload;
  const band = tariffBand(price);
  const bandLabel = { negative: t('planning.tariffNegative'), offpeak: t('planning.off'), standard: t('planning.tariffStandard'), peak: t('planning.peak') }[band];
  return (
    <div className="bg-white/95 backdrop-blur-sm border border-border/80 rounded-md px-2.5 py-1.5 shadow-md text-[11px]">
      <div className="font-semibold tabular-nums">{hour}:00</div>
      <div className="tabular-nums">{price.toLocaleString(getLocale())} {t('plan.czkPerMWh')}</div>
      <div className="text-[9px] uppercase tracking-wide text-muted-foreground">{bandLabel}</div>
    </div>
  );
}

export function OteChart() {
  const [dataset, setDataset] = useState('historical');
  const data = dataset === 'historical' ? oteHistorical : oteTypicalDay;

  const stats = useMemo(() => {
    const prices = data.hourly.map(h => h.price);
    return {
      min: Math.min(...prices),
      max: Math.max(...prices),
      avg: Math.round(prices.reduce((s, p) => s + p, 0) / prices.length),
      negativeHours: prices.filter(p => p < 0).length,
    };
  }, [data]);

  return (
    <Card className="">
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-2 flex-wrap">
          <div className="min-w-0">
            <CardTitle className="text-sm">{t('plan.oteSpotDaily')}</CardTitle>
            <p className="text-[10px] text-muted-foreground mt-0.5">{t(data.label)}</p>
          </div>
          <div className="flex gap-1 shrink-0">
            <Button
              size="sm"
              variant={dataset === 'historical' ? 'default' : 'outline'}
              className="h-6 text-[10px] px-2"
              onClick={() => setDataset('historical')}
            >
              {t('plan.oteRecord')}
            </Button>
            <Button
              size="sm"
              variant={dataset === 'typical' ? 'default' : 'outline'}
              className="h-6 text-[10px] px-2"
              onClick={() => setDataset('typical')}
            >
              {t('plan.oteTypical')}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-1">
        <div className="h-[140px] -ml-2">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data.hourly} margin={{ top: 5, right: 5, bottom: 0, left: 0 }}>
              <XAxis
                dataKey="hour"
                tick={{ fontSize: 8, fill: '#888' }}
                interval={3}
                tickLine={false}
                axisLine={{ stroke: '#e5e5e5' }}
              />
              <YAxis
                tick={{ fontSize: 8, fill: '#888' }}
                tickFormatter={(v) => v >= 1000 || v <= -1000 ? `${(v / 1000).toFixed(1)}k` : v}
                width={32}
                tickLine={false}
                axisLine={{ stroke: '#e5e5e5' }}
              />
              <ReferenceLine y={0} stroke="#999" strokeDasharray="2 2" />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
              <Bar dataKey="price" radius={[2, 2, 0, 0]}>
                {data.hourly.map((entry, i) => (
                  <Cell key={i} fill={bandColors[tariffBand(entry.price)]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="grid grid-cols-3 gap-2 mt-2 text-center">
          <div>
            <div className="text-[9px] uppercase tracking-wide text-muted-foreground">{t('plan.min')}</div>
            <div className="text-xs font-semibold tabular-nums text-blue-600">{stats.min.toLocaleString(getLocale())}</div>
          </div>
          <div>
            <div className="text-[9px] uppercase tracking-wide text-muted-foreground">{t('plan.avg')}</div>
            <div className="text-xs font-semibold tabular-nums">{stats.avg.toLocaleString(getLocale())}</div>
          </div>
          <div>
            <div className="text-[9px] uppercase tracking-wide text-muted-foreground">{t('plan.max')}</div>
            <div className="text-xs font-semibold tabular-nums text-rose-600">{stats.max.toLocaleString(getLocale())}</div>
          </div>
        </div>

        {dataset === 'historical' && stats.negativeHours > 0 && (
          <p className="text-[10px] text-blue-700 mt-2 leading-tight">
            <strong>{stats.negativeHours} h</strong> {t('plan.inNegative')} {t('plan.negativeRecordSource')}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
